import { zodResolverI18n } from '@/lib/zod-resolver-i18n'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'
import { optionalPhone } from '@/lib/phone'
import { extractErrorMessage } from '@/lib/error-message'
import { formatPropertyAddress } from '@/lib/property-address'
import { AlertCircleIcon, CheckIcon, ChevronDownIcon } from 'lucide-react'
import {
	Alert, AlertTitle, AlertDescription,
	cn,
	Form, FormControl, FormDialog, FormField, FormItem, FormLabel, FormMessage,
	PhoneInput,
	Popover, PopoverContent, PopoverTrigger,
	RadioCard,
	TextField,
} from '@rentflowdev/ui'
import { useInviteSubAccountMutation, useListPropertiesQuery } from '@/api'
import { useMemberRoles } from '@/lib/use-member-roles'
import { usePersistedForm } from '@/lib/use-persisted-form'

const inviteSchema = z.object({
	fullName:    z.string().trim().min(1),
	phone:       optionalPhone('contacts.editDialog.validation.phoneInvalid'),
	email:       z.string().trim().email().optional().or(z.literal('')),
	// A role key; the roles offered come from the server (ADR 0003).
	role:        z.string().min(1),
	propertyIds: z.array(z.string()).optional(),
})

type InviteFormValues = z.infer<typeof inviteSchema>

interface AddEmployeeDialogProps {
	open:         boolean
	onOpenChange: (open: boolean) => void
	onSuccess?:   (info: { fullName: string; email?: string; inviteLink?: string }) => void
}

export function AddEmployeeDialog({ open, onOpenChange, onSuccess }: AddEmployeeDialogProps) {
	const { t } = useTranslation()
	const [invite, { isLoading }] = useInviteSubAccountMutation()
	const [errorMsg, setErrorMsg] = useState<string | undefined>(undefined)
	const { data: propertiesData } = useListPropertiesQuery({ limit: 100 })
	const properties = propertiesData?.properties ?? []

	const { form, resetDraft } = usePersistedForm<InviteFormValues>({
		key:           'team:invite',
		defaultValues: {
			fullName:    '',
			phone:       '',
			email:       '',
			role:        'MANAGER',
			propertyIds: [],
		},
		resolver: zodResolverI18n(inviteSchema),
		mode:     'onBlur',
		active:   open,
	})

	// Only the roles the organization's segment offers; a saved draft may hold
	// one it no longer does, and then the first offered role takes its place.
	const roles = useMemberRoles()
	useEffect(() => {
		const [first] = roles
		if (open && first && !roles.some((r) => r.key === form.getValues('role'))) form.setValue('role', first.key)
	}, [open, roles, form])

	async function onSubmit(values: InviteFormValues) {
		setErrorMsg(undefined)
		try {
			const result = await invite({
				fullName:    values.fullName,
				phone:       values.phone || undefined,
				email:       values.email || undefined,
				role:        values.role,
				propertyIds: values.propertyIds && values.propertyIds.length > 0 ? values.propertyIds : undefined,
			}).unwrap()
			resetDraft()
			onOpenChange(false)
			onSuccess?.({
				fullName:   values.fullName,
				email:      values.email || undefined,
				inviteLink: (result as { inviteLink?: string } | undefined)?.inviteLink,
			})
		} catch (err) {
			console.error('inviteSubAccount failed:', err)
			setErrorMsg(extractErrorMessage(err))
		}
	}

	return (
		<Form {...form}>
			<FormDialog
				open={open}
				onOpenChange={onOpenChange}
				title={t('settings.team.invite.title')}
				onSubmit={form.handleSubmit(onSubmit)}
				isSubmitting={isLoading}
				submitLabel={t('settings.team.invite.submit')}
				cancelLabel={t('common.cancel', { defaultValue: 'Отмена' })}
				contentClassName="sm:max-w-md"
			>
				<div className="flex flex-col gap-3">
					{errorMsg !== undefined && (
						<Alert variant="destructive">
							<AlertCircleIcon className="size-4" />
							<AlertTitle>{t('settings.team.errors.invite')}</AlertTitle>
							{errorMsg && <AlertDescription>{errorMsg}</AlertDescription>}
						</Alert>
					)}
					<TextField name="fullName" label={t('settings.team.invite.fullName')} />
					{/* Explicit primitives rather than TextField: the invite phone needs
					    the masked input, and TextField renders a plain one. */}
					<FormField
						control={form.control}
						name="phone"
						render={({ field }) => (
							<FormItem>
								<FormLabel>{t('settings.team.invite.phone')}</FormLabel>
								<FormControl>
									<PhoneInput value={field.value ?? ''} onChange={field.onChange} />
								</FormControl>
								<FormMessage />
							</FormItem>
						)}
					/>
					<TextField name="email"    label={t('settings.team.invite.email')} type="email" />

					<FormField
						control={form.control}
						name="role"
						render={({ field }) => (
							<FormItem>
								<FormLabel>{t('settings.team.invite.role')}</FormLabel>
								<div className="flex flex-col gap-2">
									{roles.map((role) => (
										<RadioCard
											key={role.key}
											title={role.name}
											description={role.description ?? undefined}
											selected={field.value === role.key}
											onClick={() => field.onChange(role.key)}
											onKeyDown={(e) => e.key === 'Enter' && field.onChange(role.key)}
										/>
									))}
								</div>
								<FormMessage />
							</FormItem>
						)}
					/>

					<FormField
						control={form.control}
						name="propertyIds"
						render={({ field }) => {
							const selected = field.value ?? []
							const labelOf = (p: { id: string; city?: string; address: string }) =>
								formatPropertyAddress(p.city, p.address)
							const summary = selected
								.map((id) => properties.find((p) => p.id === id))
								.filter(Boolean)
								.map((p) => labelOf(p as { id: string; city?: string; address: string }))
								.join('; ')
							const toggle = (id: string) => {
								if (selected.includes(id)) field.onChange(selected.filter((v) => v !== id))
								else field.onChange([...selected, id])
							}
							return (
								<FormItem>
									<FormLabel>{t('settings.team.invite.objects')}</FormLabel>
									<Popover>
										<PopoverTrigger
											render={
												<button
													type="button"
													className="flex h-9 w-full items-center justify-between gap-2 rounded-(--radius-md) border border-(--color-border) bg-(--color-card) px-3 text-sm"
												/>
											}
										>
											<span className={cn('truncate', !summary && 'text-(--color-muted-foreground)')}>
												{summary || t('settings.team.invite.objects')}
											</span>
											<ChevronDownIcon className="size-4 shrink-0 text-(--color-muted-foreground)" />
										</PopoverTrigger>
										<PopoverContent className="w-(--anchor-width) max-h-72 overflow-auto rounded-(--radius-md) bg-(--color-popover) p-1 shadow-md ring-1 ring-foreground/10">
											{properties.length === 0 && (
												<p className="px-3 py-2 text-sm text-(--color-muted-foreground)">—</p>
											)}
											{properties.map((p) => {
												const checked = selected.includes(p.id)
												return (
													<button
														key={p.id}
														type="button"
														onClick={() => toggle(p.id)}
														className="flex w-full items-center gap-2 rounded-(--radius-sm) px-2 py-2 text-left text-sm hover:bg-(--color-muted)"
													>
														<CheckIcon className={cn('size-4 shrink-0', checked ? 'text-(--color-foreground)' : 'opacity-0')} />
														<span className="truncate">{labelOf(p)}</span>
													</button>
												)
											})}
										</PopoverContent>
									</Popover>
									<FormMessage />
								</FormItem>
							)
						}}
					/>

				</div>
			</FormDialog>
		</Form>
	)
}
